import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Edit2, Trash2, Save, AlertCircle, ArrowLeft } from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useToast } from '../../contexts/ToastContext';
import { useIsMobile, useIsTablet } from '../../hooks/useMediaQuery';
import { FileUploadZone } from '../shared/FileUploadZone';
import { AttachmentsList } from '../shared/AttachmentsList';
import { logger } from '../../lib/logger';

/**
 * BaseEntityModal - Shared modal infrastructure for Reuniones, Propuestas, and Gestiones
 *
 * This component handles:
 * - Modal layout (header, body, footer)
 * - Edit mode state
 * - Responsive sizing
 * - Delete confirmation
 * - Attachments (file upload + list)
 * - Animation with Framer Motion
 * - Common UI patterns
 *
 * Entity-specific logic is injected via props and render functions.
 */

export interface BaseEntityModalProps<TData> {
  // Modal state
  isOpen: boolean;
  onClose: () => void;

  // Data
  initialData?: TData | null;

  // Callbacks
  onSave?: () => void;
  onDelete?: (id: string) => Promise<void>;

  // Entity configuration
  entityType: 'reunion' | 'propuesta' | 'gestion';
  entityIcon: React.ComponentType<{ size?: number; className?: string }>;
  entityColor: string; // e.g., '#5D1828', '#B89658'
  createTitle: string;
  editTitle: string;
  createSubtitle: string;
  editSubtitle: string;

  // Save button
  saveButtonText: string;
  onSaveClick: () => Promise<void>;
  isSaveDisabled?: boolean;

  // Children (form content) - receives isEditing state from BaseEntityModal
  children: ReactNode | ((isEditing: boolean) => ReactNode);

  // Optional features
  showBackButton?: boolean;
  onBackClick?: () => void;
  backButtonTitle?: string;

  // Attachments
  entityId?: string; // Only present for existing entities
  onAttachmentsChange?: () => void;

  // Additional header actions
  renderHeaderActions?: (isEditing: boolean) => ReactNode;
}

export function BaseEntityModal<TData>({
  isOpen,
  onClose,
  initialData,
  onSave,
  onDelete,
  entityType,
  entityIcon: EntityIcon,
  entityColor,
  createTitle,
  editTitle,
  createSubtitle,
  editSubtitle,
  saveButtonText,
  onSaveClick,
  isSaveDisabled = false,
  children,
  showBackButton = false,
  onBackClick,
  backButtonTitle,
  entityId,
  onAttachmentsChange,
  renderHeaderActions,
}: BaseEntityModalProps<TData>) {
  const { currentOrg } = useOrganization();
  const toast = useToast();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const [isEditing, setIsEditing] = useState(!initialData);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [attachmentsRefreshTrigger, setAttachmentsRefreshTrigger] = useState(0);

  // Reset edit mode when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsEditing(!initialData);
    }
  }, [isOpen, initialData]);

  const handleSaveClick = async () => {
    try {
      await onSaveClick();
      setIsEditing(false);
      if (onSave) {
        onSave();
      }
      onClose();
    } catch (error) {
      logger.error(`[BaseEntityModal] Error saving ${entityType}:`, error);
      // Error toast is handled by onSaveClick
    }
  };

  const handleDelete = async () => {
    if (!initialData || !entityId || !onDelete) {
      setIsDeleteConfirmOpen(false);
      return;
    }

    try {
      await onDelete(entityId);
      setIsDeleteConfirmOpen(false);
      if (onSave) {
        onSave();
      }
      onClose();
    } catch (error) {
      logger.error(`[BaseEntityModal] Error deleting ${entityType}:`, error);
      toast.error('Error al eliminar', `No se pudo eliminar. Por favor intenta de nuevo.`);
      setIsDeleteConfirmOpen(false);
    }
  };

  if (!isOpen) return null;

  // Progressive modal sizing
  const getModalContainerPadding = () => {
    if (isMobile) return 'p-0';
    if (isTablet) return 'p-3';
    return 'p-4';
  };

  const getModalContentClass = () => {
    if (isMobile) return 'w-full h-full';
    if (isTablet) return 'rounded-xl w-[92%] max-w-xl max-h-[88vh]';
    return 'rounded-xl w-full max-w-2xl max-h-[90vh]';
  };

  return (
    <div className={`fixed inset-0 z-[110] flex items-center justify-center ${getModalContainerPadding()}`}>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-[#111827]/60 backdrop-blur-sm"
      />

      {/* Modal Content */}
      <motion.div
        initial={{ opacity: 0, scale: isMobile ? 1 : 0.95, y: isMobile ? 0 : 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: isMobile ? 1 : 0.95, y: isMobile ? 0 : 20 }}
        onClick={(e) => e.stopPropagation()}
        className={`bg-white shadow-2xl relative z-10 overflow-hidden flex flex-col border border-[#D1D5DB] ${getModalContentClass()}`}
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-[#D1D5DB] flex items-center justify-between bg-[#F9FAFB]">
          <div className="flex items-center gap-3">
            {/* Back button */}
            {showBackButton && onBackClick && (
              <button
                onClick={onBackClick}
                className="p-2 hover:bg-[#E5E7EB] rounded-full text-[#6B7280] hover:text-[#111827] transition-colors"
                title={backButtonTitle || 'Volver'}
              >
                <ArrowLeft size={20} />
              </button>
            )}

            <div>
              <h2 className="text-xl font-bold text-[#111827] flex items-center gap-2">
                <EntityIcon className={`text-[${entityColor}]`} size={24} />
                {initialData ? editTitle : createTitle}
              </h2>
              <p className="text-sm text-[#6B7280] mt-1">
                {isEditing ? createSubtitle : editSubtitle}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Additional header actions (e.g., custom buttons) */}
            {renderHeaderActions && renderHeaderActions(isEditing)}

            {/* Standard edit/delete/close buttons */}
            {initialData && !isEditing && onDelete && (
              <>
                <button
                  onClick={() => setIsDeleteConfirmOpen(true)}
                  className="p-2 hover:bg-red-50 rounded-full text-[#DC2626] hover:text-[#B91C1C] transition-colors"
                  title="Eliminar"
                >
                  <Trash2 size={18} />
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 hover:bg-[#E5E7EB] rounded-full text-[#6B7280] hover:text-[#111827] transition-colors"
                  title="Editar"
                >
                  <Edit2 size={18} />
                </button>
              </>
            )}

            <button
              onClick={onClose}
              className="p-2 hover:bg-[#E5E7EB] rounded-full text-[#6B7280] hover:text-[#111827] transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 md:p-8 overflow-y-auto custom-scrollbar">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {/* Entity-specific form fields */}
            {typeof children === 'function' ? children(isEditing) : children}

            {/* Attachments Section (only for existing entities) */}
            {entityId ? (
              <>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">
                    Subir Archivos
                  </label>
                  <FileUploadZone
                    entityType={entityType}
                    entityId={entityId}
                    onUploadComplete={() => {
                      setAttachmentsRefreshTrigger(prev => prev + 1);
                      if (onAttachmentsChange) onAttachmentsChange();
                    }}
                    disabled={false}
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">
                    Archivos Adjuntos
                  </label>
                  <AttachmentsList
                    entityType={entityType}
                    entityId={entityId}
                    onAttachmentDeleted={() => {
                      setAttachmentsRefreshTrigger(prev => prev + 1);
                      if (onAttachmentsChange) onAttachmentsChange();
                    }}
                    refreshTrigger={attachmentsRefreshTrigger}
                  />
                </div>
              </>
            ) : (
              <div className="col-span-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Archivos disponibles después de guardar</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Guarda este {entityType === 'reunion' ? 'reunión' : entityType} primero para poder adjuntar archivos.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 md:px-8 py-4 md:py-5 border-t border-[#D1D5DB] bg-[#F9FAFB] flex items-center justify-end gap-2 md:gap-3">
          <button
            onClick={onClose}
            className="px-6 py-3 md:py-2.5 min-h-[44px] rounded-lg text-[#4B5563] font-medium hover:bg-white hover:shadow-sm border border-transparent hover:border-[#D1D5DB] transition-all text-sm"
          >
            Cerrar
          </button>

          {isEditing && (
            <button
              onClick={handleSaveClick}
              disabled={isSaveDisabled}
              className={`px-6 py-3 md:py-2.5 min-h-[44px] rounded-lg font-medium shadow-lg transition-all flex items-center gap-2 text-sm text-white ${
                isSaveDisabled
                  ? 'bg-gray-400 cursor-not-allowed'
                  : `bg-[${entityColor}] hover:opacity-90`
              }`}
              style={{
                backgroundColor: isSaveDisabled ? undefined : entityColor,
                boxShadow: isSaveDisabled ? undefined : `0 10px 15px -3px ${entityColor}40`,
              }}
            >
              <Save size={18} />
              {saveButtonText}
            </button>
          )}
        </div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {isDeleteConfirmOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsDeleteConfirmOpen(false)}
              className="absolute inset-0 bg-[#111827]/60 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-sm relative z-50 p-6 flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4 text-[#DC2626]">
                <AlertCircle size={32} />
              </div>
              <h3 className="text-xl font-bold text-[#111827] mb-2">
                ¿Eliminar {entityType === 'reunion' ? 'reunión' : entityType}?
              </h3>
              <p className="text-[#6B7280] text-sm mb-6">
                Esta acción no se puede deshacer.
                {entityType === 'reunion'
                  ? ' La reunión y todas sus vinculaciones serán eliminadas permanentemente del sistema.'
                  : ` La ${entityType} será eliminada permanentemente del sistema.`
                }
              </p>
              <div className="flex w-full gap-3">
                <button
                  onClick={() => setIsDeleteConfirmOpen(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-[#D1D5DB] text-[#4B5563] font-medium hover:bg-[#F9FAFB] transition-colors text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#DC2626] text-white font-medium hover:bg-[#B91C1C] shadow-md shadow-[#DC2626]/30 transition-all text-sm"
                >
                  Eliminar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
